import { ICommand } from './ICommand';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import type { TableCellKey } from '../../stores/tableCellStore';
import type { TableCellStyle, TableCellStyleMap } from '../../domains/diagram/models/table/tableCell.types';
import { SessionMetrics } from '../../domains/analytics';
import { TABLE_SESSION_METRICS } from '../../domains/analytics/constants/MetricEvents';

interface UpdateTableCellStylesCommandParams {
  nodeIds: string[];
  cellKeysByNodeId: Record<string, TableCellKey[]>;
  stylePatch: TableCellStyle;
}

type PreviousEntry = { cellKey: TableCellKey; prev: TableCellStyle | undefined };

export class UpdateTableCellStylesCommand implements ICommand {
  id = `update-table-cell-styles-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  name = 'Update Table Cell Styles';
  timestamp = Date.now();

  private previousByNodeId: Record<string, PreviousEntry[]> = {};

  constructor(private params: UpdateTableCellStylesCommandParams) {}

  async execute(): Promise<void> {
    const mutationManager = getNodeMutationManager();

    this.previousByNodeId = {};

    const trackColorChange =
      typeof this.params.stylePatch.fillColor === 'string' ||
      typeof this.params.stylePatch.textColor === 'string' ||
      typeof this.params.stylePatch.borderColor === 'string';
    const hasTextRotation = typeof this.params.stylePatch.textRotation === 'number' && Number.isFinite(this.params.stylePatch.textRotation);

    let appliedColorChange = false;
    let appliedTextRotation = false;

    for (const nodeId of this.params.nodeIds) {
      const keys = this.params.cellKeysByNodeId[nodeId] ?? [];
      if (keys.length === 0) continue;
      const node = mutationManager.getNode(nodeId);
      if (!node) continue;
      const attrs = (node.attrs ?? {}) as Record<string, unknown>;
      const prevMap = (attrs.cellStyles as TableCellStyleMap | undefined) ?? {};
      const nextMap: TableCellStyleMap = { ...prevMap };

      const prevEntries: PreviousEntry[] = [];
      for (const k of keys) {
        prevEntries.push({ cellKey: k, prev: prevMap[k] });
        const prevStyle = prevMap[k] ?? {};
        nextMap[k] = { ...prevStyle, ...this.params.stylePatch };
      }

      this.previousByNodeId[nodeId] = prevEntries;

      const changes = { attrs: { cellStyles: nextMap } } as const;
      const record = mutationManager.applyNodeChange(nodeId, changes);
      if (record) {
        if (trackColorChange) {
          appliedColorChange = true;
        }
        if (hasTextRotation) {
          appliedTextRotation = true;
        }
      }
    }

    if (appliedColorChange) {
      SessionMetrics.increment(TABLE_SESSION_METRICS.COLORS_APPLIED);
    }
    if (appliedTextRotation) {
      SessionMetrics.increment(TABLE_SESSION_METRICS.TEXT_ROTATIONS);
    }
  }

  async undo(): Promise<void> {
    const mutationManager = getNodeMutationManager();

    for (const [nodeId, prevEntries] of Object.entries(this.previousByNodeId)) {
      const node = mutationManager.getNode(nodeId);
      if (!node) continue;
      const attrs = (node.attrs ?? {}) as Record<string, unknown>;
      const curMap = (attrs.cellStyles as TableCellStyleMap | undefined) ?? {};
      const nextMap: TableCellStyleMap = { ...curMap };

      for (const entry of prevEntries) {
        if (entry.prev) {
          nextMap[entry.cellKey] = entry.prev;
        } else {
          delete nextMap[entry.cellKey];
        }
      }

      const changes = { attrs: { cellStyles: nextMap } } as const;
      mutationManager.applyNodeChange(nodeId, changes);
    }
  }

  async redo(): Promise<void> {
    await this.execute();
  }

  canMergeWith(other: ICommand): boolean {
    if (!(other instanceof UpdateTableCellStylesCommand)) return false;
    const o = other as UpdateTableCellStylesCommand;
    return Math.abs(this.timestamp - o.timestamp) < 1000;
  }

  mergeWith(other: ICommand): ICommand {
    if (!(other instanceof UpdateTableCellStylesCommand)) return this;
    const o = other as UpdateTableCellStylesCommand;
    return new UpdateTableCellStylesCommand({
      nodeIds: Array.from(new Set([...this.params.nodeIds, ...o.params.nodeIds])),
      cellKeysByNodeId: { ...this.params.cellKeysByNodeId, ...o.params.cellKeysByNodeId },
      stylePatch: { ...this.params.stylePatch, ...o.params.stylePatch },
    });
  }

  getMetadata(): Record<string, unknown> {
    return {
      nodeIds: this.params.nodeIds,
      stylePatch: this.params.stylePatch,
    };
  }
}


